import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, ScrollText, HeartHandshake, Compass } from "lucide-react";

const DARRELL_PORTRAIT = "/brand/darrell-portrait.jpg";

const PRINCIPLES = [
  { icon: ScrollText, title: "Lived Experience", text: "Knowledge of the system from the inside — every gap, every silence, every closed door." },
  { icon: Compass, title: "Structured Guidance", text: "Attorney-graded document preparation and case support that turns confusion into a clear path." },
  { icon: HeartHandshake, title: "Empathy First", text: "Every client is met with the understanding of someone who has stood exactly where they're standing." },
];

function FounderPortrait() {
  return (
    <div className="relative w-full aspect-[3/4] bg-black border border-amber-400/30 rounded-sm overflow-hidden group" data-testid="about-portrait">
      <img
        src={DARRELL_PORTRAIT}
        alt="Darrell Graham, Founder of Paralegal Voices of New York"
        className="absolute inset-0 w-full h-full object-cover object-center"
        loading="eager"
        width="900"
        height="900"
      />
      {/* Subtle bottom gradient for caption legibility */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
      {/* Top marker */}
      <div className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.25em] text-amber-400">PORTRAIT · NY</div>
      {/* Red seal corner stamp — drawn from logo */}
      <div className="absolute top-5 right-5 flex items-center gap-2 bg-seal-red/90 backdrop-blur px-2.5 py-1 border border-amber-400/40">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-300" />
        <span className="font-mono text-[10px] tracking-[0.22em] text-amber-100">EST · NY</span>
      </div>
      {/* Caption */}
      <div className="absolute bottom-5 left-5 right-5 flex items-baseline justify-between">
        <p className="display-serif text-2xl text-zinc-50 drop-shadow-lg">Darrell Graham</p>
        <p className="font-mono text-[10px] tracking-[0.2em] text-amber-400">FOUNDER</p>
      </div>
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="border-b border-zinc-800 bg-zinc-950">
        <div className="container-pl py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <p className="overline mb-5">About</p>
              <h1 className="display-serif text-4xl sm:text-5xl lg:text-6xl text-zinc-50 leading-[1.05] mb-6">
                Darrell Graham.
              </h1>
              <p className="text-base lg:text-lg text-zinc-300 leading-relaxed font-light max-w-xl">
                Founder of Paralegal Voices of New York — and proof that purpose can be built from the
                hardest places life takes you.
              </p>
            </div>
            <div className="lg:col-span-5">
              <FounderPortrait />
            </div>
          </div>
        </div>
      </section>

      {/* STORY — formatted into short, clean sections */}
      <section className="border-b border-zinc-800" data-testid="story-section">
        <div className="container-pl py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-3">
              <div className="sticky top-24">
                <p className="overline mb-3">The Story</p>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Transformation, resilience, and a refusal to waste what was lived through.
                </p>
              </div>
            </aside>

            <div className="lg:col-span-9 space-y-10">
              <div data-testid="story-section-1">
                <p className="overline mb-3 text-zinc-400" style={{ color: "#64748b" }}>I. The Beginning</p>
                <p className="display-serif text-xl lg:text-2xl text-zinc-100 leading-[1.6] font-normal">
                  Darrell Graham didn't learn the legal system from a classroom. He learned it from
                  the inside — through firsthand experience with incarceration that revealed every
                  gap, every silence, and every door that closes when you need it open most.
                </p>
              </div>

              <div data-testid="story-section-2">
                <p className="overline mb-3 text-zinc-400" style={{ color: "#64748b" }}>II. The Awakening</p>
                <p className="display-serif text-xl lg:text-2xl text-zinc-100 leading-[1.6] font-normal">
                  Inside those walls, something shifted. Days became study sessions. Nights became
                  plans. What started as a personal need to understand his own case grew into a clear
                  conviction: if he could learn to navigate this, he could help others do the same.
                </p>
              </div>

              <div data-testid="story-section-3">
                <p className="overline mb-3 text-zinc-400" style={{ color: "#64748b" }}>III. The Work</p>
                <p className="display-serif text-xl lg:text-2xl text-zinc-100 leading-[1.6] font-normal">
                  That conviction became Paralegal Voices of New York. Darrell built the practice
                  around what he wishes had existed for him — attorney-graded document preparation,
                  structured case support, and a steady hand for those who feel unheard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE QUOTE */}
      <section className="bg-blue-700 text-white" data-testid="signature-quote">
        <div className="container-pl py-20 lg:py-24">
          <div className="max-w-4xl">
            <p className="quote-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.2] mb-8 text-white">
              "Pain births purpose. I built this practice so no one would have to face the system
              alone, unheard, or unsure of where to turn."
            </p>
            <div className="flex items-center gap-4 pt-5 border-t border-blue-700/60 max-w-sm">
              <div className="h-px flex-1 bg-blue-400/30" />
              <div className="text-right">
                <p className="display-serif text-lg text-white">Darrell Graham</p>
                <p className="text-xs text-blue-200 mt-1 font-mono tracking-[0.2em] uppercase">Founder</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-b border-zinc-800 bg-zinc-950">
        <div className="container-pl py-16 lg:py-20">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-4">What we stand on</p>
            <h2 className="display-serif text-3xl sm:text-4xl text-zinc-50 leading-tight">
              Three principles guide every case.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
            {PRINCIPLES.map((p, i) => (
              <div key={p.title} className="bg-zinc-950 p-8" data-testid={`principle-${i}`}>
                <p className="font-mono text-xs text-amber-400 mb-5 tracking-[0.2em]">0{i + 1}</p>
                <p.icon className="h-7 w-7 text-amber-400 mb-5" strokeWidth={1.25} />
                <h3 className="display-serif text-xl text-zinc-50 mb-2">{p.title}</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="border-b border-zinc-800 bg-zinc-950">
        <div className="container-pl py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-8">
              <p className="overline mb-4">The mission</p>
              <h2 className="display-serif text-3xl sm:text-4xl text-zinc-50 leading-[1.15] mb-4">
                No one should have to navigate the system{" "}
                <span className="text-amber-400">alone, unheard, or unsure.</span>
              </h2>
              <p className="text-base text-zinc-300 leading-relaxed font-light max-w-2xl">
                Whatever the case, whatever the moment — there's a voice in your corner.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right space-y-3">
              <Link to="/booking">
                <Button size="lg" className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white h-12 px-7" data-testid="about-schedule-btn">
                  Schedule a Consultation
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
              <p className="text-xs text-zinc-400">
                Or email{" "}
                <a href="mailto:Darrell@paralegalvoicesofnewyork.com" className="text-amber-400 link-underline" data-testid="about-email-link">
                  Darrell@paralegalvoicesofnewyork.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
